import { transitionsCompleted } from "@/functions";

/**
 * Implements very similar behavior to the `interestfor` attribute that is available on
 * HTML button and anchor elements natively (in a proposal stage) on chromium browsers;
 * however, the attribute will be `data-interestfor` and can target any element.
 *
 * It will also polyfill `[interestfor]`'s native functionality when not implemented
 * by the browser.
 *
 * However, the function cannot implement CSS pseudo-selectors related to the interest invoker API.
 */
export function registerInterestFor(): Disposable {
  // TODO: This was almost working, but I kept encountering an abort error
  return { [Symbol.dispose]() {} };

  const ondispose = new AbortController();
  const registered = new WeakSet<HTMLElement>();
  const observer = new MutationObserver(onmutation);
  const polyfill = !Object.hasOwn(HTMLButtonElement.prototype, "interestForElement");
  const polyfillAttributeFilter = polyfill ? ["interestfor"] : [];

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributeFilter: ["data-interestfor", ...polyfillAttributeFilter],
  });

  checkInterest(document.body);

  return {
    [Symbol.dispose]() {
      observer.disconnect();
      const error = new DOMException(
        "The interestfor registration has been disposed.",
        "ObjectDisposed",
      );
      ondispose.abort(error);
    },
  };

  function onmutation(mutations: MutationRecord[]): void {
    for (const mut of mutations) {
      if (mut.type === "attributes") {
        interestFor(mut.target);
      } else if (mut.type === "childList") {
        for (const child of mut.addedNodes) {
          checkInterest(child);
        }
      }
    }
  }

  function checkInterest(node: Node): void {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    if (node.dataset["interestfor"]) {
      interestFor(node);
    }

    if (polyfill && node.getAttribute("interestfor")) {
      interestFor(node);
    }

    for (const child of node.children) {
      checkInterest(child);
    }
  }

  function interestFor(element: Node): void {
    if (!(element instanceof HTMLElement)) {
      console.warn("The observed node is not an HTML element.", element);
      return;
    }

    if (registered.has(element)) {
      return;
    }

    registered.add(element);

    element.addEventListener("mouseover", show, { signal: ondispose.signal });
    element.addEventListener("mouseout", hide, { signal: ondispose.signal });
    element.addEventListener("focusin", show, { signal: ondispose.signal });
    element.addEventListener("focusout", hide, { signal: ondispose.signal });
  }

  function show(event: Event) {
    const element = <HTMLElement>event.currentTarget;
    const interestFor = polyfill
      ? (element.dataset["interestfor"] ?? element.getAttribute("interestfor"))
      : element.dataset["interestfor"];
    if (!interestFor) {
      console.warn("The element no longer has an interestfor data attribute.");
      element.removeEventListener("focusin", show);
      element.removeEventListener("mouseover", show);
      element.removeEventListener("focusout", hide);
      element.removeEventListener("mouseout", hide);
      return;
    }
    const interestTarget = document.getElementById(interestFor);
    if (!interestTarget) {
      return;
    }
    interestTarget.classList.remove("hiding");
    interestTarget.showPopover({ source: element });
  }

  function hide(event: Event) {
    const element = <HTMLElement>event.currentTarget;
    const interestFor = polyfill
      ? (element.dataset["interestfor"] ?? element.getAttribute("interestfor"))
      : element.dataset["interestfor"];
    if (!interestFor) {
      return;
    }
    const interestTarget = document.getElementById(interestFor);
    if (!interestTarget) {
      return;
    }

    setTimeout(async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      interestTarget.classList.add("hiding");
      await transitionsCompleted(interestTarget);
      interestTarget.hidePopover();
    });
  }
}
